import { Box, Button, Grid, Typography, Card, CardContent, Divider, MenuItem } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { FormField } from '@/components/forms/FormField';
import { tokens } from '@/theme/tokens';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { INDIAN_STATES } from '@/constants/states';

const getValidationSchema = (isEditMode) => Yup.object({
  name: Yup.string().required('Organization name is required'),
  primary_domain: Yup.string().trim(),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone is required').matches(/^\d{10}$/, 'Phone must be exactly 10 digits'),
  website: Yup.string().url('Invalid URL'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().trim().required('City is required').matches(/^[a-zA-Z\s.,'-]+$/, 'City must contain only letters'),
  state: Yup.string().trim().required('State is required').matches(/^[a-zA-Z\s.,'-]+$/, 'State must contain only letters'),
  country: Yup.string().trim().required('Country is required').matches(/^[a-zA-Z\s.,'-]+$/, 'Country must contain only letters'),
  postalCode: Yup.string().required('Postal Code is required').matches(/^\d{6}$/, 'Postal code must be exactly 6 digits'),
  ownerName: isEditMode
    ? Yup.string().trim().matches(/^[a-zA-Z\s'-]+$/, 'Owner name must contain only letters')
    : Yup.string().trim().required('Owner name is required').matches(/^[a-zA-Z\s'-]+$/, 'Owner name must contain only letters'),
  ownerEmail: isEditMode ? Yup.string().email('Invalid email') : Yup.string().email('Invalid email').required('Owner email is required'),
  ownerPhone: isEditMode ? Yup.string().matches(/^\d{10}$/, 'Phone must be exactly 10 digits') : Yup.string().required('Owner phone is required').matches(/^\d{10}$/, 'Phone must be exactly 10 digits'),
});

export function CreateOrganizationPage() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  const { 
    createOrganization, 
    updateOrganization, 
    selectedOrganization: org, 
    fetchOrganizationById,
  } = useOrganizationStore();

  useEffect(() => {
    if (isEditMode) {
      fetchOrganizationById(id);
    }
  }, [id, isEditMode, fetchOrganizationById]);

  const targetOrg = isEditMode ? org : null;
  const owner = targetOrg?.users?.find(u => u.role?.name === 'ORGANIZATION_ADMIN') 
    || targetOrg?.users?.find(u => u.role?.name === 'SCHOOL_ADMIN') 
    || targetOrg?.users?.find(u => u.role?.name === 'OWNER') 
    || targetOrg?.users?.[0] 
    || {};

  const ownerName = [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.name || '';

  const initialValues = {
    name: targetOrg?.organization_name || targetOrg?.name || '',
    primary_domain: targetOrg?.primary_domain || targetOrg?.primaryDomain || '',
    email: targetOrg?.email || '',
    phone: targetOrg?.phone || '',
    website: targetOrg?.website || '',
    address: targetOrg?.address || '',
    city: targetOrg?.city || '',
    state: targetOrg?.state || '',
    country: targetOrg?.country || 'India',
    postalCode: targetOrg?.postal_code || targetOrg?.postalCode || '',
    ownerName: ownerName,
    ownerEmail: owner.email || '',
    ownerPhone: owner.phone || '',
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        organization_name: values.name,
        primary_domain: values.primary_domain,
        email: values.email,
        phone: values.phone,
        website: values.website,
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country,
        postal_code: values.postalCode,
      };

      if (!isEditMode) {
        payload.organization_owner_fullname = values.ownerName;
        payload.organization_owner_email = values.ownerEmail;
        payload.organization_owner_phone = values.ownerPhone;
      }

      if (isEditMode) {
        await updateOrganization(id, payload);
        toast.success('Organization updated successfully');
      } else {
        await createOrganization(payload);
        toast.success('Organization created successfully');
      }
      navigate('/super-admin/organizations');
    } catch (error) {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} organization`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={isEditMode ? "Edit Organization" : "Create Organization"}
        subtitle={isEditMode ? "Update organization details." : "Onboard a new customer and set up their organization."}
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/super-admin/organizations')}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: tokens.colors.border,
              color: tokens.colors.text.primary,
            }}
          >
            Back to List
          </Button>
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(isEditMode)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form autoComplete="off">
            <Box sx={{ width: '100%' }}>
              <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main }}>
                    Organization Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="name" label="Organization Name *" placeholder="e.g. Green Valley Education Group" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="primary_domain" label="Primary Domain" placeholder="e.g. greenvalley.edu" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="email" label="Contact Email *" type="email" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="phone" label="Contact Phone *" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="website" label="Website URL" placeholder="https://" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="address" label="Street Address *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="city" label="City *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="state" label="State/Province *" select>
                        <MenuItem value=""><em>Select State</em></MenuItem>
                        {INDIAN_STATES.map((st) => (
                          <MenuItem key={st} value={st}>{st}</MenuItem>
                        ))}
                      </FormField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="country" label="Country *" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="postalCode" label="Postal Code *" />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main }}>
                    Owner Information {isEditMode && '(Created at Onboarding)'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormField name="ownerName" label="Owner Name *" disabled={isEditMode} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormField name="ownerEmail" label="Owner Email *" type="email" disabled={isEditMode} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormField name="ownerPhone" label="Owner Phone *" disabled={isEditMode} />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/super-admin/organizations')}
                      disabled={isSubmitting}
                      sx={{ borderRadius: tokens.radius.sm, px: 4, fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      startIcon={<Save size={18} />}
                      sx={{ borderRadius: tokens.radius.sm, px: 4, fontWeight: 600, boxShadow: 'none' }}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Organization'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
